import { useAnimation, useMotionValue } from 'framer-motion';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { MAX_SCALE, MIN_SCALE, ZOOM_STEP } from './index.constants';

export const useImagePreviewModal = (isOpen: boolean, onClose: () => void) => {
  const [scale, setScale] = useState(MIN_SCALE);
  const scaleRef = useRef(MIN_SCALE);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const controls = useAnimation();

  const isZoomed = scale > MIN_SCALE;

  const [dragConstraints, setDragConstraints] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  const calculateDragConstraints = useCallback(
    (targetScale?: number) => {
      const currentScale = targetScale ?? scale;
      if (
        !containerRef.current ||
        !imageRef.current ||
        currentScale <= MIN_SCALE
      ) {
        return { top: 0, bottom: 0, left: 0, right: 0 };
      }

      const container = containerRef.current.getBoundingClientRect();
      const img = imageRef.current;
      const imgRect = img.getBoundingClientRect();
      const scaledWidth = imgRect.width;
      const scaledHeight = imgRect.height;
      const overflowX = Math.max(0, (scaledWidth - container.width) / 2);
      const overflowY = Math.max(0, (scaledHeight - container.height) / 2);

      return {
        top: -overflowY,
        bottom: overflowY,
        left: -overflowX,
        right: overflowX,
      };
    },
    [scale]
  );

  const animateWithCurrentPosition = useCallback(
    (
      targetScale: number,
      options?: { resetPosition?: boolean; targetX?: number; targetY?: number }
    ): Promise<void> => {
      const shouldResetPosition =
        options?.resetPosition || targetScale === MIN_SCALE;
      const targetX = shouldResetPosition ? 0 : options?.targetX ?? x.get();
      const targetY = shouldResetPosition ? 0 : options?.targetY ?? y.get();

      return controls.start({
        x: targetX,
        y: targetY,
        scale: targetScale,
        transition: { type: 'spring', stiffness: 300, damping: 30 },
      }) as Promise<void>;
    },
    [controls, x, y]
  );

  const resetTransform = useCallback(() => {
    const resetScale = MIN_SCALE;
    setScale(resetScale);
    scaleRef.current = resetScale;
    x.set(0);
    y.set(0);
    void animateWithCurrentPosition(resetScale, { resetPosition: true });
  }, [animateWithCurrentPosition, x, y]);

  const zoomIn = useCallback(() => {
    const newScale = Math.min(scale + ZOOM_STEP, MAX_SCALE);
    setScale(newScale);
    scaleRef.current = newScale;
    void animateWithCurrentPosition(newScale).then(() => {
      requestAnimationFrame(() => {
        const constraints = calculateDragConstraints(newScale);
        setDragConstraints(constraints);
      });
    });
  }, [scale, animateWithCurrentPosition, calculateDragConstraints]);

  const zoomOut = useCallback(() => {
    const newScale = Math.max(scale - ZOOM_STEP, MIN_SCALE);
    setScale(newScale);
    scaleRef.current = newScale;

    const currentX = x.get();
    const currentY = y.get();

    let targetX = currentX;
    let targetY = currentY;

    if (newScale === MIN_SCALE) {
      targetX = 0;
      targetY = 0;
    } else if (scale > MIN_SCALE) {
      const scaleRatio = newScale / scale;
      const scaledX = currentX * scaleRatio;
      const scaledY = currentY * scaleRatio;

      const scaleRange = scale - MIN_SCALE;
      const newScaleRange = newScale - MIN_SCALE;
      const recenterFactor =
        scaleRange > 0 ? 1 - newScaleRange / scaleRange : 0;

      targetX = scaledX * (1 - recenterFactor * 0.25);
      targetY = scaledY * (1 - recenterFactor * 0.25);
    }

    void animateWithCurrentPosition(newScale, {
      targetX,
      targetY,
    }).then(() => {
      requestAnimationFrame(() => {
        const constraints = calculateDragConstraints(newScale);
        setDragConstraints(constraints);
      });
    });
  }, [scale, animateWithCurrentPosition, x, y, calculateDragConstraints]);

  const handleWheel = useCallback(
    (event: React.WheelEvent) => {
      event.preventDefault();
      const currentScale = scaleRef.current;
      const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const rawNewScale = currentScale + delta;
      const newScale = Math.min(Math.max(rawNewScale, MIN_SCALE), MAX_SCALE);

      if (newScale === currentScale) {
        return;
      }

      scaleRef.current = newScale;
      setScale(newScale);
      const currentX = x.get();
      const currentY = y.get();

      let targetX = currentX;
      let targetY = currentY;

      if (newScale === MIN_SCALE) {
        targetX = 0;
        targetY = 0;
      } else if (delta < 0 && currentScale > MIN_SCALE) {
        const scaleRatio = newScale / currentScale;
        const scaledX = currentX * scaleRatio;
        const scaledY = currentY * scaleRatio;

        const scaleRange = currentScale - MIN_SCALE;
        const newScaleRange = newScale - MIN_SCALE;
        const recenterFactor =
          scaleRange > 0 ? 1 - newScaleRange / scaleRange : 0;

        targetX = scaledX * (1 - recenterFactor * 0.25);
        targetY = scaledY * (1 - recenterFactor * 0.25);
      }

      void animateWithCurrentPosition(newScale, {
        targetX,
        targetY,
      }).then(() => {
        requestAnimationFrame(() => {
          const constraints = calculateDragConstraints(newScale);
          setDragConstraints(constraints);
        });
      });
    },
    [animateWithCurrentPosition, x, y, calculateDragConstraints]
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      if (isZoomed) {
        resetTransform();
      } else {
        const newScale = 2;
        setScale(newScale);
        scaleRef.current = newScale;
        void animateWithCurrentPosition(newScale).then(() => {
          requestAnimationFrame(() => {
            const constraints = calculateDragConstraints(newScale);
            setDragConstraints(constraints);
          });
        });
      }
    },
    [
      isZoomed,
      resetTransform,
      animateWithCurrentPosition,
      calculateDragConstraints,
    ]
  );

  const handleDragEnd = useCallback(() => {
    void controls.set({
      x: x.get(),
      y: y.get(),
      scale: scale,
    });
  }, [controls, x, y, scale]);

  useEffect(() => {
    if (!isOpen) {
      const resetScale = MIN_SCALE;
      setScale(resetScale);
      scaleRef.current = resetScale;
      x.set(0);
      y.set(0);
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomIn();
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomOut();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, zoomIn, zoomOut, x, y]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updateConstraints = () => {
      const constraints = calculateDragConstraints();
      setDragConstraints(constraints);
    };

    updateConstraints();

    const timeoutId = setTimeout(updateConstraints, 100);

    const img = imageRef.current;
    if (img) {
      if (img.complete) {
        updateConstraints();
      } else {
        img.addEventListener('load', updateConstraints);
        return () => {
          clearTimeout(timeoutId);
          img.removeEventListener('load', updateConstraints);
        };
      }
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [scale, isOpen, calculateDragConstraints]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const overlay = overlayRef.current;
    const container = containerRef.current;
    if (!overlay || !container) {
      return;
    }

    const handleWheelNative = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (container.contains(event.target as Node)) {
        const syntheticEvent = {
          deltaY: event.deltaY,
          deltaX: event.deltaX,
          deltaZ: event.deltaZ,
          deltaMode: event.deltaMode,
          preventDefault: () => {},
          stopPropagation: () => event.stopPropagation(),
          nativeEvent: event,
          currentTarget: container,
          target: event.target,
          bubbles: event.bubbles,
          cancelable: event.cancelable,
          defaultPrevented: true,
          eventPhase: event.eventPhase,
          isTrusted: event.isTrusted,
          timeStamp: event.timeStamp,
          type: event.type,
        } as unknown as React.WheelEvent<HTMLDivElement>;

        handleWheel(syntheticEvent);
      }
    };

    overlay.addEventListener('wheel', handleWheelNative, { passive: false });

    return () => {
      overlay.removeEventListener('wheel', handleWheelNative);
    };
  }, [isOpen, handleWheel]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleDocumentWheel = (event: WheelEvent) => {
      const overlay = overlayRef.current;
      if (overlay && overlay.contains(event.target as Node)) {
        event.preventDefault();
      } else if (!overlay) {
        event.preventDefault();
      }
    };

    document.addEventListener('wheel', handleDocumentWheel, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener('wheel', handleDocumentWheel, {
        capture: true,
      });
    };
  }, [isOpen]);

  return {
    overlayRef,
    containerRef,
    imageRef,
    x,
    y,
    controls,
    isZoomed,
    dragConstraints,
    zoomIn,
    zoomOut,
    handleWheel,
    handleDoubleClick,
    handleDragEnd,
  };
};
